import styles from '@brands/Dashboard/Dashboard/Provider/Sections/Schedule/styles.module.scss';
import { selectAuth } from '@brands/store/selectors/auth';
import { UserRoleName } from '@brands/services/identity/types/UserProfile';
import { formatTimeForDisplay } from '@brands/Utils/formatTime';
import { getParsedTimezoneName } from '@brands/Utils/getParsedTimezoneName';
import React, { useEffect, useState } from 'react';
import { AiOutlineCalendar } from 'react-icons/ai';
import { BsListTask } from 'react-icons/bs';

import { Loading } from '../../../../../Components/LoadingSpinner/Loading';
import { useWindowSize } from '../../../../../hooks';
import { useAppSelector } from '../../../../../hooks/useReduxHook';
import {
  ListProviderAvailabilitiesResponse,
  listUserAvailabilities,
} from '../../../../../services/availability/listUserAvailabilities';
import { ProviderAvailability } from '../../../../../services/availability/types/ProviderAvailability';
import { ICase } from '../../../../../services/cases/types/ICase';
import AlertModal from './Components/AlertModal/AlertModal';
import DashboardCalendar from './Components/DashboardCalendar/DashboardCalendar';
import DateOverrides from './Components/DateOverrides/DateOverrides';
import SelectDate, { OverrideHour } from './Components/SelectDateModal/SelectDate';
import WeeklyHours from './Components/WeeklyHours/WeeklyHours';
import Divider from '@mui/material/Divider/Divider';
export const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const Schedule = (): JSX.Element => {
  const screenSize = useWindowSize();
  const { userInfo } = useAppSelector(selectAuth);
  const currentTimezone = getParsedTimezoneName(userInfo?.address?.timezone ?? 'EST');
  const [view, setView] = useState('list');
  const [addDateModal, setAddDateModal] = useState(false);
  const [alertModal, setAlertModal] = useState<{
    cases: ICase[];
    kind: 'override' | 'weekly';
    type: 'delete' | 'update' | 'add';
    selectedDates?: Date[];
    isUnavailable?: boolean;
    slots: ProviderAvailability[];
    overrideSlots?: OverrideHour[];
  } | null>(null);
  const [selectedDates, setSelectedDates] = useState<Date[] | null>(null);
  const [editingOverrideDates, setEditingOverrideDates] = useState<Date[] | null>(null);
  const [weeklyAvailabilities, setWeeklyAvailabilities] = useState<ProviderAvailability[]>([]);
  const [dateOverrides, setDateOverrides] = useState<ProviderAvailability[]>([]);
  const [loading, setLoading] = useState(false);
  const [requestSending, setRequestSending] = React.useState(false);
  const [hoursTypeSelection, setHoursTypeSelection] = useState<'weekly' | 'date'>('weekly');

  const getAvailabilityList = async (type: 'weekday' | 'date'): Promise<ListProviderAvailabilitiesResponse> => {
    const params = {
      user_id: userInfo.id,
      type,
    };
    const availabilityList = await listUserAvailabilities(userInfo.id, params);
    return availabilityList;
  };

  const convertTimeFormat = (time: string): string => formatTimeForDisplay(time);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      setLoading(true);
      const processNodes = (nodes: ProviderAvailability[]): ProviderAvailability[] =>
        nodes.map((node) => ({
          ...node,
          start_time: convertTimeFormat(node.start_time),
          end_time: convertTimeFormat(node.end_time),
        }));

      const { nodes } = await getAvailabilityList('weekday');
      setWeeklyAvailabilities(processNodes(nodes));

      const { nodes: dateNodes } = await getAvailabilityList('date');
      setDateOverrides(processNodes(dateNodes));

      setLoading(false);
    };

    fetchData();
  }, []);

  const handleEditOverride = (overrideDates: Date[]): void => {
    setEditingOverrideDates(overrideDates);
    setSelectedDates(overrideDates);
    setAddDateModal(true);
  };

  if (loading) return <Loading dotted />;

  const weeklyHoursComponent = (
    <div className={styles.dateSelectionColumn}>
      <WeeklyHours
        weeklyHours={weeklyAvailabilities}
        setWeeklyHours={setWeeklyAvailabilities}
        setAlertModal={setAlertModal}
      />
    </div>
  );

  const dateOverridesComponent = (
    <div className={styles.dateSelectionColumn} style={{ maxWidth: '500px' }}>
      <DateOverrides
        dateOverrides={dateOverrides}
        setDateOverrides={setDateOverrides}
        setAddDateModal={setAddDateModal}
        setAlertModal={setAlertModal}
        requestSending={requestSending}
        onEditOverride={handleEditOverride}
      />
    </div>
  );

  const dateSelectionColumns = (
    <div className={styles.dateSelectionColumns}>
      <>
        {weeklyHoursComponent}
        {dateOverridesComponent}
      </>
    </div>
  );

  return (
    <>
      <div className={styles.scheduleSectionContainer}>
        <span className={styles.scheduleSectionContainerHeader}>Set your availability</span>
        <div className={styles.viewSelection}>
          <div className={`fs-unmask ${styles.viewSelectionTimeZoneDiv}`}>
            <div className={`fs-unmask ${styles.viewSelectionTimeZone}`}>Timezone</div>
            <div className={`fs-unmask ${styles.viewSelectionTimeZoneValue}`}>{currentTimezone}</div>
          </div>
          <div className={styles.viewSelectionButtonsContainer}>
            <button
              className={`${styles.viewSelectionButton} fs-unmask ${
                view === 'list' ? styles.viewSelectionButtonActive : ''
              }`}
              onClick={() => setView('list')}
              type="button"
            >
              <BsListTask size="1.25rem" />
              List view
            </button>
            <button
              className={`${styles.viewSelectionButton} fs-unmask ${
                view === 'calendar' ? styles.viewSelectionButtonActive : ''
              }`}
              onClick={() => setView('calendar')}
              type="button"
            >
              <AiOutlineCalendar size="1.25rem" />
              Calendar view
            </button>
          </div>
        </div>
        <Divider className={styles.muiDivider} />
        {view === 'list' ? (
          <>{dateSelectionColumns}</>
        ) : (
          <>
            <div className={`${styles.dateSelectionColumns} mt-0 pb-5 fs-unmask`}>
              <div className={styles.calendarContainer}>
                <DashboardCalendar
                  weeklyHours={weeklyAvailabilities}
                  dateOverrides={dateOverrides}
                  setDateOverrides={setDateOverrides}
                  setView={setView}
                  setAddDateModal={setAddDateModal}
                  setSelectedDates={setSelectedDates}
                  setAlertModal={setAlertModal}
                />
              </div>
            </div>
          </>
        )}
        {userInfo.role.name === UserRoleName.Provider && (
          <div className={styles.availabilityInfo}>
            <p className={styles.intro}>
              As a provider on our platform, you can customize your availability for both synchronous (real-time)
              and asynchronous (message-based) appointments.
            </p>
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Asynchronous Appointments (Message-Based)</div>
              <p className={styles.sectionDescription}>
                These appointments allow patients to submit cases for review at any time, you must respond within 24
                hours.
              </p>
              <ul>
                <li>
                  Photo Reviews – Patients upload images (e.g., tooth concerns, restorations) for your assessment.
                </li>
                <li>
                  Second Opinions – Patients submit existing diagnoses or treatment plans for your professional input.
                </li>
              </ul>
            </div>

            <div className={styles.section}>
              <div className={styles.sectionTitle}>Synchronous Appointments (Real-Time)</div>
              <p className={styles.sectionDescription}>
                These are live, scheduled video or phone consultations where you interact directly with patients. Ideal
                for:
              </p>
              <ul>
                <li>
                  Talk Live with a Dentist – Real-time discussions for consultations, urgent concerns, or treatment
                  planning.
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
      {addDateModal && (
        <SelectDate
          addDateModal={addDateModal}
          setAddDateModal={setAddDateModal}
          dateOverrides={dateOverrides}
          setDateOverrides={setDateOverrides}
          weeklyAvailabilities={weeklyAvailabilities}
          selectedDates={selectedDates}
          setSelectedDates={setSelectedDates}
          timezone={currentTimezone}
          setAlertModal={setAlertModal}
          setRequestSending={setRequestSending}
          editingOverrideDates={editingOverrideDates}
          setEditingOverrideDates={setEditingOverrideDates}
        />
      )}
      {alertModal && (
        <AlertModal
          isOpen={alertModal !== null}
          toggleModal={() => setAlertModal(null)}
          overlaps={alertModal}
          setValues={alertModal.kind === 'weekly' ? setWeeklyAvailabilities : setDateOverrides}
        />
      )}
    </>
  );
};

export default Schedule;
